import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { PAPER_BASE } from './config';
import PaperSEO from '../../components/paper/PaperSEO';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const PER_PAGE = 30;

const CATEGORY_LABEL = {
  FESTIVAL: '축제',
  EVENT: '공연',
};

const FILTERS = [
  { key: '', label: '전체' },
  { key: 'FESTIVAL', label: '축제' },
  { key: 'EVENT', label: '공연' },
];

export default function PaperGalleryPage() {
  const [images, setImages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [lightbox, setLightbox] = useState(null); // index of currently open image
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);

  const fetchImages = useCallback(async (pageNum, category, reset = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const params = { page: pageNum, limit: PER_PAGE };
      if (category) params.category = category;
      const res = await axios.get(`${API}/api/paperWork/gallery`, { params });
      const newImages = res.data.images || [];
      setImages(prev => reset ? newImages : [...prev, ...newImages]);
      setHasMore(pageNum < res.data.totalPages);
    } catch {
      // ignore
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  // Initial load & filter change
  useEffect(() => {
    setImages([]);
    setPage(1);
    setHasMore(true);
    fetchImages(1, filter, true);
  }, [filter, fetchImages]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingRef.current) {
          setPage(prev => prev + 1);
        }
      },
      { rootMargin: '400px' }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Load more when page changes (skip page 1, handled by filter effect)
  useEffect(() => {
    if (page > 1 && hasMore) {
      fetchImages(page, filter);
    }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightbox === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight' && lightbox < images.length - 1) setLightbox(lightbox + 1);
      if (e.key === 'ArrowLeft' && lightbox > 0) setLightbox(lightbox - 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox, images.length]);

  // Lock body scroll when lightbox open
  useEffect(() => {
    if (lightbox !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightbox]);

  const handleFilter = (key) => {
    if (key === filter) return;
    setFilter(key);
  };

  const currentImg = lightbox !== null ? images[lightbox] : null;

  return (
    <div className="min-h-screen">
      <PaperSEO
        title="갤러리"
        description="핑크페이퍼의 프로젝트 현장을 사진으로 만나보세요"
        path="/gallery"
      />

      {/* Header */}
      <section className="pt-10 pb-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h1 className="text-white text-2xl font-bold mb-1">갤러리</h1>
          <p className="text-slate-500 text-sm mb-6">프로젝트 현장의 순간들</p>

          {/* Filter */}
          <div className="flex gap-2">
            {FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => handleFilter(f.key)}
                className={`px-4 py-1.5 rounded-full text-sm transition-all ${
                  filter === f.key
                    ? 'bg-violet-500 text-white'
                    : 'bg-white/5 text-white/40 hover:text-white/60 hover:bg-white/10'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Masonry Grid */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {images.length === 0 && !loading ? (
            <div className="text-center py-20 text-white/30">등록된 사진이 없습니다</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {images.map((img, idx) => (
                <button
                  key={img.id ?? idx}
                  onClick={() => setLightbox(idx)}
                  className="group relative overflow-hidden rounded-xl w-full text-left cursor-pointer aspect-square"
                >
                  <img
                    src={img.imageUrl}
                    alt={img.caption || img.work?.title || ''}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    loading="lazy"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                    {img.work?.category && (
                      <span className="inline-block self-start bg-violet-500/80 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full mb-1.5">
                        {CATEGORY_LABEL[img.work.category] || img.work.category}
                      </span>
                    )}
                    <p className="text-white text-sm font-medium leading-tight line-clamp-2">
                      {img.work?.title}
                    </p>
                    {img.caption && (
                      <p className="text-white/50 text-xs mt-1 line-clamp-1">{img.caption}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex justify-center py-10">
              <div className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin" />
            </div>
          )}

          {/* Sentinel for infinite scroll */}
          {hasMore && <div ref={sentinelRef} className="h-1" />}
        </div>
      </section>

      {/* Lightbox */}
      {currentImg && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          {/* Close */}
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white/60 hover:text-white hover:bg-white/20 transition-all"
          >
            <X size={20} />
          </button>

          {/* Prev */}
          {lightbox > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }}
              className="absolute left-3 sm:left-6 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white/60 hover:text-white hover:bg-white/20 transition-all"
            >
              <ChevronLeft size={22} />
            </button>
          )}

          {/* Next */}
          {lightbox < images.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }}
              className="absolute right-3 sm:right-6 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white/60 hover:text-white hover:bg-white/20 transition-all"
            >
              <ChevronRight size={22} />
            </button>
          )}

          {/* Image + Info */}
          <div
            className="flex flex-col items-center max-w-[90vw] max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentImg.imageUrl}
              alt={currentImg.caption || currentImg.work?.title || ''}
              className="max-w-full max-h-[78vh] object-contain rounded-lg"
            />

            {/* Bottom Info */}
            <div className="mt-4 flex items-center gap-3 flex-wrap justify-center">
              {currentImg.work && (
                <Link
                  to={`${PAPER_BASE}/work/${currentImg.work.id}`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 hover:text-violet-200 transition-all text-sm"
                  onClick={() => setLightbox(null)}
                >
                  <ExternalLink size={14} />
                  {currentImg.work.title}
                  {currentImg.work.category && (
                    <span className="text-violet-400/60 text-xs ml-1">
                      ({CATEGORY_LABEL[currentImg.work.category]})
                    </span>
                  )}
                </Link>
              )}
              {currentImg.caption && (
                <span className="text-white/40 text-xs">{currentImg.caption}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
